import type { ProvidesLine } from "@sins2-atlas/game-data";

// One Provides line's value, as a surface rendering ONE unit or four draws it — the entity
// page's card and the comparison's rows, where the label is the row header (Issue 30,
// CONTEXT.md: Provides). The dense table draws the whole group as one cell instead
// (ProvidesCell below, ADR 0008 addendum).
// A captioned line carries its Assumption Caption the way a captioned stat cell does — the
// dotted underline and cursor:help that mean "there is honesty language one layer down"
// (the Argonev's factory exists only with Construction Bays installed). No line is no
// content at all: an empty cell, never a glyph, because Provides is a list of facts and
// ADR 0003's states are for stats.
//
// A line with no value at all is a whole statement the game already words over a file that
// carries no figure (the Phase Gate's "Phase Gate in Gravity Well", Issue 31). Its label is
// the row header and the cell affirms it with a check — presentation, not a word of ours —
// with the label itself read out for anyone not seeing the mark.
export function ProvidesValue({ line }: { line: ProvidesLine | undefined }) {
  if (line === undefined) return null;
  const body =
    line.value === undefined ? (
      <>
        <span aria-hidden="true">✓</span>
        <span className="sr-only">{line.label}</span>
      </>
    ) : (
      line.value
    );
  if (line.caption === undefined) return <span>{body}</span>;
  return (
    <span
      className="cursor-help underline decoration-muted decoration-dotted underline-offset-2"
      title={line.caption}
    >
      {body}
    </span>
  );
}

// The Structures table's Provides cell (Issue 33, ADR 0008 addendum): the structure's own
// lines in the dataset's order, each game label beside its value. The label lives here
// rather than in a column head because a column per label was 14 columns holding 31 filled
// cells — every game word survives, it just moved (ADR 0005 is about which words, not where
// they sit). The game itself prints "Warfare Research Points   1", label then figure with no
// punctuation between, so this does too.
//
// A line with no value is its own whole statement ("Phase Gate in Gravity Well") and reads
// as its label alone: the check mark the other surfaces draw affirms a row header, and here
// there is no row header to affirm. A unit with no lines is an empty cell, never a glyph —
// Provides is a list of facts and ADR 0003's states are for stats.
//
// Each line CLAMPS to three lines of text here, and only here (user decision 2026-09-06).
// Since "Can Build Ships" began naming the ships rather than their build kinds, the longest
// value is 22 names: unclamped it made a 582 px row against the table's 62 px median, so
// every other column's figure floated in a wall of ship names. The value is unchanged and
// the clamp is presentation — the whole list is on the entity page and in a comparison
// column, both of which give a list the room it needs, and the title attribute carries it
// here for a pointer. Three lines is the largest clamp that keeps a Provides row within a
// two-line stat row's height.
export function ProvidesCell({ lines }: { lines: readonly ProvidesLine[] }) {
  if (lines.length === 0) return null;
  return (
    <ul className="space-y-1">
      {lines.map((line) => {
        const body = (
          <>
            {/* A real space, not only the margin: the label and the value are one sentence
                to a screen reader and to anyone copying the cell, and adjacent inline spans
                with no whitespace between them read as "Can Build ShipsCorvette". */}
            <span className="text-muted">{line.label}</span>{" "}
            {line.value !== undefined && <span className="ml-0.5">{line.value}</span>}
          </>
        );
        return (
          <li
            key={line.label}
            className="line-clamp-3 leading-snug"
            // ONE title for the cell, carrying both things the reader cannot see: the value
            // the clamp cut off, and the Assumption Caption. The other surfaces put the
            // caption on its own span, but here that span would win the hover and hide the
            // clamped text — and the worst-clamped line in the table (the Orkulus's 22
            // ships) is a captioned one, so that is exactly the case it would fail.
            title={[line.value, line.caption].filter(Boolean).join(" — ") || undefined}
          >
            {line.caption === undefined ? (
              body
            ) : (
              // The same Assumption Caption affordance as everywhere else: dotted underline
              // and cursor:help mean there is honesty language one layer down. The title
              // itself lives on the row above, so the two never compete for the pointer.
              <span className="cursor-help underline decoration-muted decoration-dotted underline-offset-2">
                {body}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
